/**
 * Staff Portal v2 — main shell.
 *
 * Single-file composition of sidebar (desktop), header (top), mobile
 * bottom nav, and mobile drawer. Kept in one file to avoid over-splitting
 * for a foundation phase. Sub-components can be extracted later if any
 * grows past ~120 lines.
 *
 * Feature-flag contract:
 *   • `staff_portal_v2_enabled = false`  → this component renders children
 *     with NO chrome (transparent passthrough). Legacy page-level layouts
 *     continue to work unchanged. This is the rollback path.
 *   • `staff_portal_v2_enabled = true`   → shell chrome renders around
 *     children. Navigation visibility uses `staff_navigation_v2_enabled`.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { useStaffV2Flags } from '@/lib/staffV2Flags';
import {
  StaffNotificationsProvider,
  useStaffNotifications,
} from '@/contexts/StaffNotificationsContext';
import { PRIMARY_NAV, MOBILE_BOTTOM_NAV, filterNavByPermission } from './navigationConfig';
import { GlobalSearch } from '@/components/shared/GlobalSearch';
import api from '@/lib/api';
import {
  Menu, X, Bell, ChevronRight, ChevronDown, ChevronsLeft, ChevronsRight,
  Clock, AlertTriangle, FileText, LogOut, User as UserIcon, ClipboardCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { roleLabel } from '@/lib/roleLabels';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 68;

/** Header notification button — reads shared unread state. */
function HeaderNotificationBadge() {
  const { unreadCount } = useStaffNotifications();
  const display = unreadCount > 99 ? '99+' : String(unreadCount);
  const label = unreadCount === 0
    ? 'Notifications'
    : `Notifications, ${unreadCount} unread`;
  return (
    <Button asChild variant="ghost" size="sm" data-testid="staff-v2-header-notifications">
      <Link to="/notifications" aria-label={label} className="relative">
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 font-semibold flex items-center justify-center"
            aria-hidden="true"
            data-testid="staff-v2-header-unread-badge"
          >
            {display}
          </span>
        )}
      </Link>
    </Button>
  );
}

/** Header handover button — unread count from the Phase 1 endpoint. */
function HeaderHandoverBadge() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const r = await api.get('/handover/unread-count');
        if (live) setCount(r.data?.data?.unread_count || 0);
      } catch { /* endpoint unavailable — badge stays hidden */ }
    };
    load();
    window.addEventListener('staff-v2-handover-ack', load);
    window.addEventListener('focus', load);
    return () => {
      live = false;
      window.removeEventListener('staff-v2-handover-ack', load);
      window.removeEventListener('focus', load);
    };
  }, []);
  const label = count === 0 ? 'Handovers' : `Handovers, ${count} unacknowledged`;
  return (
    <Button asChild variant="ghost" size="sm" data-testid="staff-v2-header-handovers">
      <Link to="/staff/handovers" aria-label={label} className="relative">
        <ClipboardCheck className="h-4 w-4" />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] leading-4 font-semibold flex items-center justify-center"
            aria-hidden="true"
            data-testid="staff-v2-handover-unread-badge"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </Link>
    </Button>
  );
}

function useLearningBadge(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) { setCount(0); return undefined; }
    let live = true;
    const load = () => api.get('/staff/learning/summary')
      .then((r) => { if (live) setCount(r.data?.data?.badge_count || 0); })
      .catch(() => { /* flag off or unavailable — badge stays hidden */ });
    load();
    window.addEventListener('staff-v2-learning-changed', load);
    return () => { live = false; window.removeEventListener('staff-v2-learning-changed', load); };
  }, [enabled]);
  return count;
}

/** Sidebar brand mark — organisation logo with graceful initials fallback + optional
 * name. Mirrors the Admin AppShell branding treatment (reuses the same
 * branding.logoUrl / branding.organisationName source). */
function BrandMark({ branding, showName = true, testIdPrefix = 'staff-v2' }) {
  const [imgFailed, setImgFailed] = useState(false);
  const name = branding.organisationName || 'TrinitiCare';
  const initials = (name.trim().split(/\s+|(?<=[a-z])(?=[A-Z])/).filter(Boolean).map((w) => w[0]).slice(0, 3).join('') || 'TC').toUpperCase();
  const showLogo = !!branding.logoUrl && !imgFailed;
  return (
    <span className={`flex items-center gap-2 min-w-0 ${showName ? '' : 'justify-center'}`}>
      {showLogo ? (
        <img
          src={branding.logoUrl}
          alt={name}
          className="h-7 w-7 rounded-md object-contain bg-white/15 p-0.5 flex-shrink-0"
          onError={() => setImgFailed(true)}
          data-testid={`${testIdPrefix}-brand-logo`}
        />
      ) : (
        <span className="w-7 h-7 rounded-md bg-white/15 text-white flex items-center justify-center text-xs font-bold flex-shrink-0" aria-hidden="true" data-testid={`${testIdPrefix}-brand-initials`}>{initials}</span>
      )}
      {showName && <span className="font-bold text-white text-base tracking-tight truncate" data-testid={`${testIdPrefix}-brand-name`}>{name}</span>}
    </span>
  );
}

function StaffPortalShellInner({ children, title, subtitle }) {
  const flags = useStaffV2Flags();
  const { user, hasPermission, logout } = useAuth();
  const branding = useBranding();
  const location = useLocation();
  const navigate = useNavigate();

  // Canonical platform branding — same source the Admin shell uses so every
  // role/portal shares one brand colour. Falls back to the spec'd navy.
  const sidebarBg = branding.primaryColour || '#0B2545';

  // Collapsed sidebar preference persists across sessions.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('staffV2.sidebar.collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('staffV2.sidebar.collapsed', collapsed ? '1' : '0'); } catch (_) { /* localStorage unavailable */ }
  }, [collapsed]);

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  // Escape closes drawer + returns focus to the mobile menu button.
  useEffect(() => {
    if (!mobileDrawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMobileDrawerOpen(false);
        const btn = document.querySelector('[data-testid="staff-v2-mobile-menu"]');
        if (btn) btn.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileDrawerOpen]);
  const [expandedSubmenu, setExpandedSubmenu] = useState(() => {
    // Auto-expand My Shifts when the user is inside any of its child routes.
    return location.pathname.startsWith('/my/') || location.pathname.startsWith('/timesheets/') ||
           location.pathname.startsWith('/staff/roster') || location.pathname.startsWith('/staff/open-shifts') ||
           location.pathname.startsWith('/portal/leave') || location.pathname.startsWith('/portal/availability')
      ? 'my-shifts' : null;
  });

  const navItems = useMemo(() => filterNavByPermission(PRIMARY_NAV, hasPermission), [hasPermission]);
  const learningBadge = useLearningBadge(!!flags.staff_learning_enabled);
  const showChrome = flags.staff_portal_v2_enabled;
  const showNav = flags.staff_navigation_v2_enabled;

  // Rollback path — flag off → pass children through unchanged.
  if (!showChrome) return children;

  const isActive = (to) => {
    if (!to) return false;
    if (to === '/staff/dashboard') return location.pathname === '/staff/dashboard';
    return location.pathname === to || location.pathname.startsWith(to + '/');
  };

  const handleMobileMore = () => setMobileDrawerOpen(true);
  const handleMobileNav = (item) => {
    if (item.to) navigate(item.to);
    else if (item.key === 'm-more') handleMobileMore();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="staff-v2-shell">
      {/* Skip-to-content — accessibility */}
      <a href="#staff-v2-main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-white focus:px-3 focus:py-2 focus:rounded">
        Skip to main content
      </a>

      {/* Desktop sidebar — approved dark-navy design */}
      {showNav && (
        <aside
          className="hidden md:flex md:flex-col fixed inset-y-0 left-0 z-30 border-r border-white/10 transition-[width] duration-200 motion-reduce:transition-none"
          style={{ width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH, backgroundColor: sidebarBg, '--sidebar-bg': sidebarBg }}
          aria-label="Primary navigation"
          data-testid="staff-v2-sidebar"
        >
          <div className={`flex border-b border-white/10 ${collapsed ? 'flex-col items-center gap-2 py-3 px-2' : 'h-14 items-center justify-between px-4'}`}>
            <BrandMark branding={branding} showName={!collapsed} />
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="p-1.5 rounded hover:bg-white/10 text-white/80"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              data-testid="staff-v2-sidebar-toggle"
            >
              {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto py-3">
            <ul className="space-y-0.5 px-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.to);
                const hasSubmenu = item.submenu && item.submenu.length > 0;
                const visibleSubs = hasSubmenu ? filterNavByPermission(item.submenu, hasPermission) : [];
                const expanded = expandedSubmenu === item.key && !collapsed && visibleSubs.length > 0;
                return (
                  <li key={item.key}>
                    <div className="flex items-stretch">
                      <Link
                        to={item.to || '#'}
                        onClick={(e) => {
                          if (hasSubmenu && !item.to) e.preventDefault();
                          if (hasSubmenu && !collapsed) {
                            setExpandedSubmenu((cur) => (cur === item.key ? null : item.key));
                          }
                        }}
                        className={`flex-1 flex items-center gap-3 px-3 py-2 rounded text-sm font-medium transition-colors ${
                          active
                            ? 'bg-white text-[var(--sidebar-bg,#0B2545)] font-medium'
                            : 'text-white/90 hover:bg-white/10 hover:text-white'
                        }`}
                        title={collapsed ? item.label : undefined}
                        aria-current={active ? 'page' : undefined}
                        data-testid={item.testId}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                        {!collapsed && item.key === 'learning' && learningBadge > 0 && (
                          <span className="ml-auto min-w-[18px] text-center text-[10px] font-semibold bg-white/25 text-white rounded-full px-1.5 py-0.5"
                                data-testid="nav-learning-badge"
                                aria-label={`Learning, ${learningBadge} item${learningBadge > 1 ? 's' : ''} need attention`}>
                            {learningBadge > 99 ? '99+' : learningBadge}
                          </span>
                        )}
                        {!collapsed && hasSubmenu && visibleSubs.length > 0 && (
                          expanded ? <ChevronDown className="h-3.5 w-3.5 ml-auto" /> : <ChevronRight className="h-3.5 w-3.5 ml-auto" />
                        )}
                      </Link>
                    </div>
                    {expanded && (
                      <ul className="mt-0.5 mb-1 ml-4 pl-3 border-l border-white/15 space-y-0.5" data-testid={`${item.testId}-submenu`}>
                        {visibleSubs.map((sub) => {
                          const SubIcon = sub.icon;
                          const subActive = isActive(sub.to);
                          return (
                            <li key={sub.key}>
                              <Link
                                to={sub.to}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded text-[13px] transition-colors ${
                                  subActive
                                    ? 'bg-white/15 text-white font-medium'
                                    : 'text-white/70 hover:text-white'
                                }`}
                                aria-current={subActive ? 'page' : undefined}
                                data-testid={sub.testId}
                              >
                                <SubIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="truncate">{sub.label}</span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
          {/* Sidebar footer — staff profile area (approved design) */}
          <div className="p-3 border-t border-white/10" data-testid="staff-v2-sidebar-profile">
            {collapsed ? (
              <div className="w-8 h-8 mx-auto rounded-full bg-white/15 text-white flex items-center justify-center text-xs font-semibold" title={user?.full_name}>
                {(user?.full_name || 'S').split(' ').map((w) => w[0]).slice(0, 2).join('')}
              </div>
            ) : (
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0" aria-hidden="true">
                  {(user?.full_name || 'S').split(' ').map((w) => w[0]).slice(0, 2).join('')}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-white truncate" data-testid="staff-v2-sidebar-profile-name">{user?.full_name || 'Staff'}</div>
                  <div className="text-[11px] text-white/60 truncate" data-testid="staff-v2-sidebar-profile-role">{roleLabel(user?.role_name) || ''}</div>
                </div>
                <Link to="/staff/employment" className="p-1.5 rounded hover:bg-white/10 text-white/80" aria-label="My profile" data-testid="staff-v2-sidebar-profile-link">
                  <UserIcon className="h-4 w-4" />
                </Link>
                <button onClick={() => logout && logout()} className="p-1.5 rounded hover:bg-white/10 text-white/80" aria-label="Sign out" data-testid="staff-v2-sidebar-logout">
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </aside>
      )}

      {/* Mobile drawer */}
      {mobileDrawerOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
            onClick={() => setMobileDrawerOpen(false)}
            aria-hidden="true"
          />
          <div
            className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] shadow-xl md:hidden flex flex-col"
            style={{ backgroundColor: sidebarBg, '--sidebar-bg': sidebarBg }}
            role="dialog"
            aria-modal="true"
            aria-label="Staff menu"
            data-testid="staff-v2-mobile-drawer"
          >
            <div className="h-14 flex items-center justify-between px-4 border-b border-white/10">
              <BrandMark branding={branding} showName testIdPrefix="staff-v2-mobile" />
              <button
                onClick={() => setMobileDrawerOpen(false)}
                className="p-2 -mr-2 rounded hover:bg-white/10 text-white/80"
                aria-label="Close menu"
                data-testid="staff-v2-mobile-drawer-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-3" aria-label="All staff sections">
              <ul className="space-y-0.5">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.key}>
                      <Link
                        to={item.to || '#'}
                        onClick={() => setMobileDrawerOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium ${
                          isActive(item.to) ? 'bg-white text-[var(--sidebar-bg,#0B2545)] font-medium' : 'text-white/90 hover:bg-white/10'
                        }`}
                        data-testid={`mobile-${item.testId}`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{item.label}</span>
                        {item.key === 'learning' && learningBadge > 0 && (
                          <span className="ml-auto min-w-[18px] text-center text-[10px] font-semibold bg-white/25 text-white rounded-full px-1.5 py-0.5"
                                data-testid="mobile-nav-learning-badge">{learningBadge > 99 ? '99+' : learningBadge}</span>
                        )}
                      </Link>
                      {item.submenu && filterNavByPermission(item.submenu, hasPermission).map((sub) => {
                        const SubIcon = sub.icon;
                        return (
                          <Link
                            key={sub.key}
                            to={sub.to}
                            onClick={() => setMobileDrawerOpen(false)}
                            className="flex items-center gap-3 pl-10 pr-3 py-2 rounded text-[13px] text-white/70 hover:bg-white/10"
                            data-testid={`mobile-${sub.testId}`}
                          >
                            <SubIcon className="h-3.5 w-3.5" />
                            <span>{sub.label}</span>
                          </Link>
                        );
                      })}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
        </>
      )}

      {/* Main column — offset by the fixed sidebar width on md+ */}
      <div
        className="flex flex-col min-h-screen md:pl-[var(--staff-sbw)] transition-[padding] duration-200 motion-reduce:transition-none"
        style={{ '--staff-sbw': `${showNav ? (collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH) : 0}px` }}
      >
        <div className="flex flex-col min-h-screen">
        {/* Header */}
        <header
          className="sticky top-0 z-20 h-14 bg-card border-b border-border flex items-center gap-2 px-3 md:px-6"
          style={{ marginLeft: 0 }}
          data-testid="staff-v2-header"
        >
          {/* Mobile menu trigger */}
          <button
            onClick={() => setMobileDrawerOpen(true)}
            className="md:hidden p-2 -ml-2 rounded hover:bg-muted"
            aria-label="Open menu"
            data-testid="staff-v2-mobile-menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex-1 min-w-0">
            {title && <h1 className="text-base md:text-lg font-semibold truncate" data-testid="staff-v2-page-title">{title}</h1>}
            {subtitle && <p className="text-xs text-muted-foreground truncate hidden md:block" data-testid="staff-v2-page-subtitle">{subtitle}</p>}
          </div>

          {/* Quick actions — permission-aware */}
          <div className="flex items-center gap-1">
            <GlobalSearch />
            {(hasPermission('attendance.clock_in') || hasPermission('attendance.clock_out')) && (
              <Button asChild variant="ghost" size="sm" data-testid="staff-v2-quick-clock">
                <Link to="/my/clock" aria-label="Clock In or Out">
                  <Clock className="h-4 w-4" />
                  <span className="hidden xl:inline ml-2">Clock In/Out</span>
                </Link>
              </Button>
            )}
            {hasPermission('incidents.create') && (
              <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex" data-testid="staff-v2-quick-incident">
                <Link to="/compliance/incidents/new" aria-label="Report Incident">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="hidden xl:inline ml-2">Report Incident</span>
                </Link>
              </Button>
            )}
            {hasPermission('notes.create') && (
              <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex" data-testid="staff-v2-quick-note">
                <Link to="/my/work" aria-label="Add Progress Note">
                  <FileText className="h-4 w-4" />
                  <span className="hidden xl:inline ml-2">New Note</span>
                </Link>
              </Button>
            )}

            {/* Handovers */}
            <HeaderHandoverBadge />

            {/* Notifications */}
            <HeaderNotificationBadge />

            {/* Profile menu */}
            <div className="relative">
              <button
                onClick={() => setProfileMenuOpen((o) => !o)}
                className="flex items-center gap-2 pl-2 pr-1 py-1 rounded hover:bg-muted"
                aria-haspopup="menu"
                aria-expanded={profileMenuOpen}
                data-testid="staff-v2-profile-trigger"
              >
                <div className="w-7 h-7 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-semibold">
                  {(user?.full_name || 'S').split(' ').map((w) => w[0]).slice(0, 2).join('')}
                </div>
                <span className="hidden lg:inline text-sm max-w-[120px] truncate">{user?.full_name || 'Staff'}</span>
              </button>
              {profileMenuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setProfileMenuOpen(false)} aria-hidden="true" />
                  <div className="absolute right-0 mt-1 w-56 bg-popover rounded-md shadow-lg border border-border z-40 py-1" role="menu" data-testid="staff-v2-profile-menu">
                    <div className="px-3 py-2 border-b border-border">
                      <div className="text-sm font-medium truncate">{user?.full_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{roleLabel(user?.role_name)}</div>
                    </div>
                    <Link to="/staff/employment" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted" role="menuitem" onClick={() => setProfileMenuOpen(false)}>
                      <UserIcon className="h-4 w-4" /> My Employment
                    </Link>
                    <button
                      onClick={() => { setProfileMenuOpen(false); logout && logout(); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted"
                      role="menuitem"
                      data-testid="staff-v2-profile-logout"
                    >
                      <LogOut className="h-4 w-4" /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Main content */}
        <main
          id="staff-v2-main"
          className="flex-1 w-full px-3 md:px-6 py-4 md:py-6 pb-24 md:pb-8"
          data-testid="staff-v2-main"
          tabIndex={-1}
        >
          {children}
        </main>
        </div>
      </div>

      {/* Mobile bottom navigation */}
      {showNav && (
        <nav
          className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-card border-t border-border flex justify-around h-16 pb-[env(safe-area-inset-bottom)]"
          aria-label="Mobile navigation"
          data-testid="staff-v2-bottom-nav"
        >
          {MOBILE_BOTTOM_NAV.map((item) => {
            const Icon = item.icon;
            const active = item.to ? isActive(item.to) : false;
            return (
              <button
                key={item.key}
                onClick={() => handleMobileNav(item)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1 min-h-[44px] ${
                  active ? 'text-primary' : 'text-muted-foreground'
                }`}
                aria-current={active ? 'page' : undefined}
                data-testid={item.testId}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}

/**
 * Public shell entry — wraps the inner shell in the notification provider
 * so header badge + dashboard panel + notifications page share one
 * unread count. Provider mounts once per shell instance (i.e. per
 * authenticated user session on any wrapped route).
 */
export default function StaffPortalShell(props) {
  return (
    <StaffNotificationsProvider>
      <StaffPortalShellInner {...props} />
    </StaffNotificationsProvider>
  );
}
